import * as store from '@attain-apps/fe/web/main/lib/store'
import * as rxjs from 'rxjs'

import type { Collection } from '../../../../collection'
import type * as ecModels from '../../../../models'
import type { Plugin } from '../plugin'

/**
 * Updates an entity
 */
export const executeUpdate = store.effects.create(
  <TTypeParams extends ecModels.TypeParams.Constraint>(
    feature: Plugin<TTypeParams>['feature'],
    collection: Collection<TTypeParams>,
  ) => {
    const canUpdate = collection.plugins.apiExecutionPlugin.canExecute('update')
    const canBatchUpdate =
      collection.plugins.apiExecutionPlugin.canExecute('batchUpdate')
    if (!canUpdate && !canBatchUpdate) {
      // TODO: automatically cancel every request
      return undefined
    }

    return feature.actions.update.relay((forwardTo, updates) =>
      collection.feature.selectors.context.asObservable.pipe(
        // NOTE: we only want to take the first context. This is really
        // important since otherwise the relay method does not know when we are
        // done sending instructions
        rxjs.take(1),
        rxjs.exhaustMap(context => {
          if ((updates.length > 1 && canBatchUpdate) || !canUpdate) {
            // If this is a batch (more than one), and we can batch update, or
            // if we can't update so we have no other option than to batch
            // update, then we just submit one batch update request
            return rxjs.of(
              forwardTo(
                collection.plugins.apiExecutionPlugin.feature.actions.execute,
                {
                  input: {
                    args: [updates],
                    context,
                    methodName: 'batchUpdate',
                  },
                  output: ({ response }) =>
                    response as VoidToUndefined<
                      rxjs.ObservedValueOf<
                        ecModels.Api.Method.Result<
                          'batchUpdate',
                          typeof collection.api
                        >
                      >
                    >,
                },
              ),
            )
          } else {
            // Otherwise, we submit a update request for each update
            return rxjs.from(updates).pipe(
              rxjs.map(update =>
                forwardTo(
                  collection.plugins.apiExecutionPlugin.feature.actions.execute,
                  {
                    input: {
                      args: [update],
                      context,
                      methodName: 'update',
                    },
                    output: ({ response }, previousUpdates) => {
                      const entity = response as VoidToUndefined<
                        rxjs.ObservedValueOf<
                          ecModels.Api.Method.Result<
                            'update',
                            typeof collection.api
                          >
                        >
                      >
                      return entity
                        ? previousUpdates
                          ? previousUpdates.concat([entity])
                          : [entity]
                        : previousUpdates
                    },
                  },
                ),
              ),
            )
          }
        }),
      ),
    ).asObservable
  },
)
